import { clinicNAP, type ClinicNAP } from "./clinic";

type OgType = "website" | "article";

interface SeoOptions {
  title: string;
  description: string;
  path: string;
  ogType?: OgType;
}

export const SITE_URL = "https://www.blepharoplasty.drvaheclinic.com";
const SITE_NAME = clinicNAP.name;

const upsertByName = (name: string, content: string) => {
  let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertByProperty = (property: string, content: string) => {
  let el = document.querySelector(
    `meta[property="${property}"]`,
  ) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertCanonical = (href: string) => {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

/**
 * Apply title, description, canonical and Open Graph / Twitter metadata.
 * Safe to call inside a useEffect on mount.
 */
export const applySeo = ({ title, description, path, ogType = "website" }: SeoOptions) => {
  document.title = title;

  const url = `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;

  upsertByName("description", description);
  upsertCanonical(url);

  // Open Graph
  upsertByProperty("og:title", title);
  upsertByProperty("og:description", description);
  upsertByProperty("og:type", ogType);
  upsertByProperty("og:url", url);
  upsertByProperty("og:site_name", SITE_NAME);

  // Twitter
  upsertByName("twitter:card", "summary_large_image");
  upsertByName("twitter:title", title);
  upsertByName("twitter:description", description);
};

/**
 * Inject (or replace) a JSON-LD structured-data block in <head>.
 * Pass an `id` so the same block can be safely replaced on re-render
 * and removed on unmount.
 */
export const applyJsonLd = (
  id: string,
  data: Record<string, unknown> | Record<string, unknown>[],
) => {
  const elementId = `jsonld-${id}`;
  let el = document.getElementById(elementId) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = elementId;
    document.head.appendChild(el);
  }
  el.text = JSON.stringify(data);
  return () => {
    const existing = document.getElementById(elementId);
    if (existing) existing.remove();
  };
};

const dayMap: Record<string, string> = {
  Mo: "Monday",
  Tu: "Tuesday",
  We: "Wednesday",
  Th: "Thursday",
  Fr: "Friday",
  Sa: "Saturday",
  Su: "Sunday",
};

const SHARED_AREA_SERVED = [
  { "@type": "City", name: "London" },
  { "@type": "AdministrativeArea", name: "Greater London" },
  { "@type": "Country", name: "United Kingdom" },
];

const SHARED_IMAGE =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/IBo7X3t19XTGsumpVFkqpYNqnx23/social-images/social-1777281190200-Blepharolplasty.webp";

/**
 * Build the full site-wide JSON-LD @graph for a given clinic snapshot.
 * Called by SiteSchema on every live-settings change so the structured
 * data on the page always matches the visible NAP.
 */
export const buildSiteGraph = (clinic: ClinicNAP) => {
  const oneLineAddress = [
    clinic.address.street,
    clinic.address.locality,
    `${clinic.address.region} ${clinic.address.postalCode}`,
    clinic.address.country,
  ].join(", ");

  const mapsHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${clinic.name} ${clinic.address.street} ${clinic.address.postalCode}`,
  )}`;

  const SHARED_ADDRESS = {
    "@type": "PostalAddress",
    streetAddress: clinic.address.street,
    addressLocality: clinic.address.locality,
    addressRegion: clinic.address.region,
    postalCode: clinic.address.postalCode,
    addressCountry: clinic.address.countryCode,
  };

  const SHARED_GEO = {
    "@type": "GeoCoordinates",
    latitude: clinic.geo.latitude,
    longitude: clinic.geo.longitude,
  };

  const SHARED_OPENING_HOURS = clinic.hours
    .filter((r) => r.opens && r.closes)
    .map((r) => ({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: r.schema.map((d) => dayMap[d]),
      opens: r.opens,
      closes: r.closes,
    }));

  const SAME_AS = [clinic.social.website, clinic.social.instagram].filter(Boolean);

  const CLINIC_NODE = {
    "@type": ["LocalBusiness", "MedicalOrganization", "MedicalClinic"],
    "@id": `${SITE_URL}/#clinic`,
    name: clinic.name,
    alternateName: clinic.alternateName,
    url: SITE_URL,
    image: SHARED_IMAGE,
    logo: `${SITE_URL}/favicon.ico`,
    telephone: clinic.phone.international,
    priceRange: "££££",
    medicalSpecialty: ["PlasticSurgery", "Ophthalmologic"],
    areaServed: SHARED_AREA_SERVED,
    address: SHARED_ADDRESS,
    geo: SHARED_GEO,
    hasMap: mapsHref,
    openingHoursSpecification: SHARED_OPENING_HOURS,
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "reservations",
        telephone: clinic.phone.international,
        availableLanguage: ["English"],
        areaServed: "GB",
      },
      ...(clinic.whatsapp.number
        ? [
            {
              "@type": "ContactPoint",
              contactType: "customer support",
              telephone: clinic.whatsapp.number,
              url: clinic.whatsapp.url,
              availableLanguage: ["English"],
              areaServed: "GB",
              contactOption: "TollFree",
            },
          ]
        : []),
    ],
    sameAs: SAME_AS,
  };

  const PHYSICIAN_NODE = {
    "@type": ["Physician", "IndividualPhysician"],
    "@id": `${SITE_URL}/#dr-vahe`,
    name: "Dr Vahe",
    jobTitle: "Aesthetic & Oculoplastic Surgeon",
    worksFor: { "@id": `${SITE_URL}/#clinic` },
    medicalSpecialty: ["PlasticSurgery", "Ophthalmologic"],
    areaServed: SHARED_AREA_SERVED,
    url: SITE_URL,
    image: SHARED_IMAGE,
  };

  const BLEPHAROPLASTY_SERVICES = [
    {
      "@type": ["MedicalProcedure", "Service"],
      "@id": `${SITE_URL}/#service-upper-blepharoplasty`,
      name: "Upper Blepharoplasty",
      alternateName: "Upper Eyelid Surgery",
      procedureType: "https://schema.org/SurgicalProcedure",
      bodyLocation: "Upper eyelid",
      howPerformed:
        "Performed under local anaesthesia in a central London accredited surgical suite, typically in 45–60 minutes.",
      preparation: "Pre-operative consultation and medical assessment by Dr Vahe.",
      followup: "Stitches removed on day 7; review appointments scheduled at 1 and 6 weeks.",
      provider: { "@id": `${SITE_URL}/#clinic` },
      serviceType: "Blepharoplasty",
      areaServed: SHARED_AREA_SERVED,
      offers: {
        "@type": "Offer",
        price: "2300",
        priceCurrency: "GBP",
        availability: "https://schema.org/InStock",
        description: "Preferred promotional rate, subject to consultation and availability.",
        url: `${SITE_URL}/#booking`,
      },
    },
    {
      "@type": ["MedicalProcedure", "Service"],
      "@id": `${SITE_URL}/#service-lower-blepharoplasty`,
      name: "Lower Blepharoplasty",
      alternateName: "Lower Eyelid Surgery",
      procedureType: "https://schema.org/SurgicalProcedure",
      bodyLocation: "Lower eyelid",
      howPerformed:
        "Performed in a central London accredited surgical suite to address under-eye bags and excess lower-lid skin.",
      provider: { "@id": `${SITE_URL}/#clinic` },
      serviceType: "Blepharoplasty",
      areaServed: SHARED_AREA_SERVED,
    },
    {
      "@type": ["MedicalProcedure", "Service"],
      "@id": `${SITE_URL}/#service-upper-lower-blepharoplasty`,
      name: "Upper & Lower Blepharoplasty",
      alternateName: "Combined Eyelid Surgery",
      procedureType: "https://schema.org/SurgicalProcedure",
      bodyLocation: "Upper and lower eyelids",
      howPerformed:
        "Combined upper and lower eyelid procedure performed in 90–120 minutes under local anaesthesia with optional sedation.",
      provider: { "@id": `${SITE_URL}/#clinic` },
      serviceType: "Blepharoplasty",
      areaServed: SHARED_AREA_SERVED,
      offers: {
        "@type": "Offer",
        price: "3100",
        priceCurrency: "GBP",
        availability: "https://schema.org/InStock",
        description: "Preferred promotional rate, subject to consultation and availability.",
        url: `${SITE_URL}/#booking`,
      },
    },
  ];

  const BLEPHAROPLASTY_FAQ = {
    "@type": "FAQPage",
    "@id": `${SITE_URL}/#faq`,
    mainEntity: [
      {
        "@type": "Question",
        name: "What is blepharoplasty?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Blepharoplasty is eyelid surgery that removes or repositions excess skin, muscle and fat around the upper or lower eyelids to create a more rested, naturally vibrant appearance.",
        },
      },
      {
        "@type": "Question",
        name: `Where is ${clinic.name} based?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: `${clinic.name} is located at ${oneLineAddress}. Consultations and surgery take place by appointment only in our central London accredited surgical suite.`,
        },
      },
      {
        "@type": "Question",
        name: "How much does blepharoplasty cost in London?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Current promotional rates start from £2,300 for upper blepharoplasty and £3,100 for combined upper and lower blepharoplasty. Treatment is subject to consultation and availability. Klarna instalment plans are available.",
        },
      },
      {
        "@type": "Question",
        name: "How long does blepharoplasty take?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Upper blepharoplasty typically takes 45–60 minutes under local anaesthesia. Combined upper and lower blepharoplasty takes 90–120 minutes. Sedation options are available if preferred.",
        },
      },
      {
        "@type": "Question",
        name: "What is recovery from eyelid surgery like?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Stitches are removed on day 7. Most patients are comfortable returning to social engagements within 7–10 days, with subtle residual swelling fully resolving by week three.",
        },
      },
    ],
  };

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        url: SITE_URL,
        name: clinic.name,
        publisher: { "@id": `${SITE_URL}/#clinic` },
        inLanguage: "en-GB",
      },
      CLINIC_NODE,
      PHYSICIAN_NODE,
      ...BLEPHAROPLASTY_SERVICES,
      BLEPHAROPLASTY_FAQ,
    ],
  };
};

/** Backwards-compat: snapshot built from the static defaults. */
export const SITE_GRAPH = buildSiteGraph(clinicNAP);
const _defaultGraph = buildSiteGraph(clinicNAP);
export const BLEPHAROPLASTY_SERVICES = (_defaultGraph["@graph"] as Array<Record<string, unknown>>)
  .filter((n) => Array.isArray(n["@type"]) && (n["@type"] as string[]).includes("MedicalProcedure"));
export const BLEPHAROPLASTY_FAQ = (_defaultGraph["@graph"] as Array<Record<string, unknown>>)
  .find((n) => n["@type"] === "FAQPage");
export const CLINIC_ENTITY = (_defaultGraph["@graph"] as Array<Record<string, unknown>>)
  .find((n) => Array.isArray(n["@type"]) && (n["@type"] as string[]).includes("LocalBusiness"));

